import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Suspense, lazy } from 'react';
import * as Sentry from '@sentry/react';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import { AuthProvider } from './components/auth/AuthProvider';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Home from './pages/Home';
import LoginPage from './pages/admin/LoginPage';
import AuthCallback from './pages/admin/AuthCallback';
import DashboardPage from './pages/admin/DashboardPage';
import UserAdministrationPage from './pages/admin/UserAdministrationPage';
import BusinessAdministrationPage from './pages/admin/BusinessAdministrationPage';
import MarketplaceAdministrationPage from './pages/admin/MarketplaceAdministrationPage';
import UnauthorizedPage from './pages/admin/UnauthorizedPage';

// Lazy-loaded admin pages
const FraudDashboardPage = lazy(() => import('./pages/admin/FraudDashboardPage'));

// Code splitting for legal pages (lazy loading)
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const DataDeletion = lazy(() => import('./pages/DataDeletion'));
const NotFound = lazy(() => import('./pages/NotFound'));

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

// Loading fallback component
function PageLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
        <p className="text-neutral-600">Loading...</p>
      </div>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <AuthProvider>
          <Router>
            <Suspense fallback={<PageLoading />}>
            <SentryRoutes>
              <Route path="/" element={<Home />} />
              
              {/* Admin Routes */}
              <Route path="/admin/login" element={<LoginPage />} />
              <Route path="/admin/auth/callback" element={<AuthCallback />} />
              <Route 
                path="/admin/dashboard" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <DashboardPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/users" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <UserAdministrationPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/users/:userId" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <UserAdministrationPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/businesses" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <BusinessAdministrationPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/businesses/:businessId" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <BusinessAdministrationPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/businesses/:businessId/edit" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <BusinessAdministrationPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/marketplace" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <MarketplaceAdministrationPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/marketplace/:itemId" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <MarketplaceAdministrationPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/marketplace/:itemId/edit" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <MarketplaceAdministrationPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/fraud" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <FraudDashboardPage />
                  </ProtectedRoute>
                } 
              />
              <Route path="/admin/unauthorized" element={<UnauthorizedPage />} />
              
              {/* Legal Pages */}
              <Route
                path="/privacy-policy"
                element={
                  <Layout>
                    <PrivacyPolicy />
                  </Layout>
                }
              />
              <Route
                path="/terms-of-service"
                element={
                  <Layout>
                    <TermsOfService />
                  </Layout>
                }
              />
              <Route
                path="/cookie-policy"
                element={
                  <Layout>
                    <CookiePolicy />
                  </Layout>
                }
              />
              <Route
                path="/data-deletion"
                element={
                  <Layout>
                    <DataDeletion />
                  </Layout>
                }
              />
              
              <Route path="*" element={<NotFound />} />
            </SentryRoutes>
          </Suspense>
          </Router>
        </AuthProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
